import { useEffect, useMemo, useRef, useState } from "react";
import type { AppEvent } from "./types";
import { useSettings } from "../settings/useSettings";
import { normalizeCourseKey } from "../../shared/utils/normalizeCourse";
import { fetchCalendarIcs } from "./api";
import { parseIcs } from "./parseIcs";
import { keepOnlyDeadlines } from "./ics/filterDeadlines";
import { filterHiddenCourses } from "./selectors";
import { loadGroupScheduleAsEvents } from "./group/groupSource";
import { useToasts } from "../../shared/components/Toast";
import { useT } from "../i18n/useT";

function sortEvents(a: AppEvent, b: AppEvent) {
  const ta = new Date(a.due || a.start).getTime();
  const tb = new Date(b.due || b.start).getTime();
  return ta - tb;
}

let lastStatusGlobal: "ok" | "error" | null = null;
let lastToastAt = 0;

export function useEvents(options?: { includeOnline?: boolean }): AppEvent[] {
  const [settings] = useSettings();
  const [raw, setRaw] = useState<AppEvent[]>([]);
  const timerRef = useRef<number | null>(null);
  const { show } = useToasts();
  const t = useT();

  useEffect(() => {
    let alive = true;

    async function run() {
      const isIcsOnly = settings.scheduleSource === "ics";
      const icsUrl = (settings.icsUrl || "").trim();
      const groupName = (settings.selectedGroup || "").trim();
      let didFetch = false;

      try {
        let icsEvents: AppEvent[] = [];
        let groupEvents: AppEvent[] = [];

        if (icsUrl) {
          try {
            const icsText = await fetchCalendarIcs(icsUrl);
            if (!icsText || !icsText.trim().startsWith("BEGIN:VCALENDAR")) {
              const trimmed = (icsText || "").trim();
              if (trimmed.startsWith("{")) {
                try {
                  const obj = JSON.parse(trimmed);
                  const msg = obj?.message || obj?.code || "Invalid ICS response";
                  throw new Error(`ICS proxy error: ${msg}`);
                } catch {
                  // fall through
                }
              }
              const snippet = trimmed.slice(0, 200);
              throw new Error(`Invalid ICS format received from server: ${snippet}`);
            }
            icsEvents = keepOnlyDeadlines(parseIcs(icsText));
            didFetch = true;
          } catch (e) {
            console.warn("useEvents ICS load failed", e);
          }
        } else if (isIcsOnly) {
          if (alive) setRaw([]);
          return;
        }

        if (!isIcsOnly) {
          if (!groupName) {
            if (alive && !icsEvents.length) setRaw([]);
            return;
          }

          try {
            groupEvents = await loadGroupScheduleAsEvents(groupName);
            didFetch = true;
          } catch (e) {
            console.warn("useEvents GROUP load failed", e);
          }
        }

    let merged: AppEvent[] = [];
        if (isIcsOnly) {
          merged = icsEvents;
        } else {
          const deadlines = icsEvents.filter((e) => e.kind === "deadline" || e.kind === "assignment");
          const lessons = groupEvents.filter((e) => e.kind === "lesson");
          merged = [...lessons, ...deadlines];
        }

        if (alive && didFetch) setRaw(merged);

        if (icsUrl && didFetch && lastStatusGlobal !== "ok") {
          const now = Date.now();
          if (now - lastToastAt > 3000) {
            lastStatusGlobal = "ok";
            lastToastAt = now;
            show(t("common.connection.ok"), undefined, 5000, "success");
          }
        }
      } catch (e) {
        console.warn("useEvents load failed", e);

        const now = Date.now();
        if (icsUrl && lastStatusGlobal !== "error" && now - lastToastAt > 3000) {
          lastStatusGlobal = "error";
          lastToastAt = now;
          show(t("common.connection.fail"), undefined, 5000, "danger");
        }
      }
    }

    run();

    const onDuRefresh = () => run();
    window.addEventListener("du:refresh", onDuRefresh);

    if (timerRef.current) window.clearInterval(timerRef.current);

    const intervalMs = Math.max(1, settings.autoRefreshMinutes || 30) * 60_000;
    timerRef.current = window.setInterval(() => run(), intervalMs);

    return () => {
      alive = false;
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
      window.removeEventListener("du:refresh", onDuRefresh);
    };
  }, [
    settings.scheduleSource,
    settings.icsUrl,
    settings.selectedGroup,
    settings.autoRefreshMinutes,
  ]);

  const filtered = useMemo(() => {
    let next = filterHiddenCourses(raw, settings.hiddenCourses || [], normalizeCourseKey);

    if (settings.hideOnlineLessons && !options?.includeOnline) {
      next = next.filter((e) => {
        if (e.kind !== "lesson") return true;
        const room = (e.room || "").toLowerCase();
        return !(room.includes("online") || room.includes("онлайн"));
      });
    }

    return next.slice().sort(sortEvents);
  }, [raw, settings.hiddenCourses, settings.hideOnlineLessons, options?.includeOnline]);

  return filtered;
}
